import { useWallet } from "@manahippo/aptos-wallet-adapter";
import React, { useEffect, useMemo, useState } from "react";

import { getCoinBalance, getTransactionByHash } from "../../libs/aptos_sdk";
import { Pool, getPool } from "../../libs/pool";
import {
  add_liquidity,
  get_coin_icon,
  get_coin_short_name,
} from "../../libs/swap_sdk";
import { useAppSelector } from "../../store/hooks";
import { sleep } from "../../utils/common";
import Button from "../Common/Button";
import Input from "../Common/Input";
import TransactionDialog from "../Common/TransactionDialog";
import Divider from "../Divider";

type Props = {
  coinX: string;
  coinY: string;
  slippageRate: string;
};

const DepositTab: React.FC<Props> = (props: Props) => {
  const { coinX, coinY, slippageRate } = props;
  const { signAndSubmitTransaction } = useWallet();
  const accountAddress = useAppSelector(
    (state: any) => state.wallet.accountAddress
  );

  const coinInfoIconX = get_coin_icon(coinX);
  const coinInfoIconY = get_coin_icon(coinY);
  const [depositText, setDepositText] = useState("Enter an amount");

  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [step, setStep] = useState(0);
  const [txn, setTxn] = useState("");

  const [amountX, setAmountX] = useState("");
  const [amountY, setAmountY] = useState("");

  const [balanceX, setBalanceX] = useState("0");
  const [balanceY, setBalanceY] = useState("0");
  const [pool, setPool] = useState<Pool>();

  useEffect(() => {
    const fetch = async () => {
      if (coinX && coinY) {
        const data = await getPool(coinX, coinY);
        if (data) setPool(data);
      }
    };
    fetch();
    const timer = setInterval(fetch, 10 * 1000);
    return () => {
      clearInterval(timer);
    };
  }, [coinX, coinY]);

  useMemo(() => {
    const get = async () => {
      const amountX = await getCoinBalance(accountAddress, coinX);
      if (Number(amountX) > 0) {
        // @ts-ignore
        setBalanceX(Number(amountX));
      }
    };
    get();
  }, [accountAddress, coinX]);

  useMemo(() => {
    const get = async () => {
      const amountY = await getCoinBalance(accountAddress, coinY);
      if (Number(amountY) > 0) {
        // @ts-ignore
        setBalanceY(Number(amountY));
      }
    };
    get();
    const timer = setInterval(get, 17000);
    return () => {
      clearInterval(timer);
    };
  }, [accountAddress, coinY]);

  const handleChangeAmountX = (input: any) => {
    const v = input.target.value;
    setAmountX(v);
    if (pool) {
      const amount_y =
        ((Number(pool.reserve_y) * Number(v)) / Number(pool.reserve_x)) *
        Math.pow(10, pool.x_coin_info.decimals - pool.y_coin_info.decimals);
      setAmountY(amount_y.toFixed(5));
    }
  };

  const handleChangeAmountY = (input: any) => {
    const v = input.target.value;
    setAmountY(v);
    if (pool) {
      const amount_x =
        ((Number(pool.reserve_x) * Number(v)) / Number(pool.reserve_y)) *
        Math.pow(10, pool.y_coin_info.decimals - pool.x_coin_info.decimals);
      setAmountX(amount_x.toFixed(5));
    }
  };
  const handleCloseAlert = () => setOpenAlert(false);

  const handleAdd = async () => {
    if (!pool) return;
    setOpenAlert(true);
    setTxn("");
    setStep(0);

    const xAmount1 = parseInt(
      (Number(amountX) * Math.pow(10, pool.x_coin_info.decimals)).toFixed(0),
      10
    );
    const yAmount1 = parseInt(
      (Number(amountY) * Math.pow(10, pool.y_coin_info.decimals)).toFixed(0),
      10
    );

    const xAmount1_min = parseInt(
      (xAmount1 * (1 - Number(slippageRate) / 100)).toFixed(),
      10
    );
    const yAmount1_min = parseInt(
      (yAmount1 * (1 - Number(slippageRate) / 100)).toFixed(),
      10
    );

    const data = await add_liquidity(
      signAndSubmitTransaction,
      coinX,
      coinY,
      xAmount1,
      yAmount1,
      xAmount1_min,
      yAmount1_min
    );
    setTxn(data.hash);

    setStep(1);
    let transaction;
    while (!transaction) {
      try {
        transaction = await getTransactionByHash(data.hash);
        window.console.info("transaction", transaction);
      } catch (e) {
        await sleep(500);
        window.console.error(e);
      }
    }
    setStep(2);
  };

  useEffect(() => {
    if (Number(amountX) <= 0 && Number(amountY) <= 0) {
      setDepositText("Enter an amount");
    } else if (amountX > balanceX) {
      setDepositText(`Insufficient  ${get_coin_short_name(coinX)} balance`);
    } else if (amountY > balanceY) {
      setDepositText(`Insufficient  ${get_coin_short_name(coinY)} balance`);
    } else {
      setDepositText("Deposit");
    }
  }, [amountX, amountY, coinX, coinY, balanceX, balanceY]);

  return (
    <>
      <div className="p-4">
        <div className="flex flex-col">
          <div className="flex justify-between items-start rounded-xl bg-primary-100 gap-2 p-4">
            <div className="flex flex-col justify-end items-end">
              <Input
                type="number"
                value={amountX}
                placeholder="0.00"
                onChange={handleChangeAmountX}
              />
              {/* <p className="text-white">$: --</p> */}
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center gap-2 bg-primary-200 rounded-full p-2">
                <img alt="Coin" className="w-6 h-6" src={coinInfoIconX.icon} />
                <span className="text-white text-[15px] font-semibold">
                  {get_coin_short_name(coinX)}
                </span>
              </div>
              <div className="text-white text-opacity-50">
                Balance:
                <span className="text-[#2196f3] ml-3">
                  {Number(balanceX).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
          <Divider>+</Divider>
          <div className="flex justify-between items-start rounded-xl bg-primary-100 gap-2 p-4">
            <div className="flex flex-col justify-end items-end">
              <Input
                type="number"
                value={amountY}
                placeholder="0.00"
                onChange={handleChangeAmountY}
              />
              {/* <p className="text-white">$: --</p> */}
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center gap-2 bg-primary-200 rounded-full p-2">
                <img alt="Coin" className="w-6 h-6" src={coinInfoIconY.icon} />
                <span className="text-white text-[15px] font-semibold">
                  {get_coin_short_name(coinY)}
                </span>
              </div>
              <div className="text-white text-opacity-50">
                Balance:
                <span className="text-[#2196f3] ml-3">
                  {Number(balanceY).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 text-white text-opacity-50 pt-4">
            <div>Prices and pool share</div>

            <div className="flex justify-between items-center">
              <p>
                1 {get_coin_short_name(coinX)} per {get_coin_short_name(coinY)}
              </p>
              <p>
                1 {get_coin_short_name(coinY)} per {get_coin_short_name(coinX)}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="p-4">
        <Button
          fullWidth
          onClick={handleAdd}
          disabled={depositText !== "Deposit"}
          variant="contained"
        >
          {depositText}
        </Button>
      </div>

      <div>
        <TransactionDialog
          openAlert={openAlert}
          txn={txn}
          title=""
          step={step}
          handleCloseAlert={handleCloseAlert}
        />
      </div>
    </>
  );
};

export default DepositTab;
